import type { ApplicationLayerData } from '../types/application'
import type { CoResidentTabsProps } from '../types/components'
import { Link } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { formatApplicantId } from '../utils/applicationFormatters'
import { RiskRatingBadge } from './RiskRatingBadge'
import { usePermission } from '@/hooks/usePermission'

export function CoResidentTabs({
  coresidents,
  currentApplicantId,
  onCoResidentClick,
  relationshipTypes,
  statusMap,
  actions,
}: CoResidentTabsProps) {
  const { hasPolicy } = usePermission()
  const showApplicantScreeningApproach = hasPolicy([
    'application_detail_applicant_screening_approach',
  ])
  const showApplicantRiskRating = hasPolicy(['application_detail_applicant_risk_rating'])

  const sortedCoresidents = [...coresidents].sort((a, b) => {
    if (a.is_primary) return -1
    if (b.is_primary) return 1
    return 0
  })

  const handleNavigate = (
    e: React.MouseEvent,
    applicantId: string | number | undefined,
  ) => {
    e.preventDefault()
    if (applicantId == null) return
    onCoResidentClick(String(applicantId))
  }

  const livingInUnitValue = (co: ApplicationLayerData): string | null => {
    if (co.is_primary) return null
    if (!['0', '1'].includes(co.living_unit || '')) return null
    return co.living_unit === '1' ? 'Yes' : 'No'
  }

  const relationshipLabel = (co: ApplicationLayerData): string | null => {
    const relationshipType = (
      co.user_data as { relationship_type?: string } | undefined
    )?.relationship_type
    if (co.is_primary || !relationshipType) return null
    return relationshipTypes[relationshipType] || relationshipType
  }

  return (
    <section
      className="rounded-2xl border border-slate-300 bg-white p-3 shadow-sm"
      data-testid="application-detail-co-resident-tabs"
    >
      {actions ? (
        <div className="mb-3 flex flex-wrap items-center justify-end gap-2 rounded-xl bg-slate-50 p-2">
          {actions}
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {sortedCoresidents.map((co, index) => {
          const isActive = String(co.applicant_id) === currentApplicantId
          const isLocked = co.applicant_lock != null && String(co.applicant_lock) === '1'
          const rel = relationshipLabel(co)
          const livingValue = livingInUnitValue(co)
          // Property-approved applicants are not underwritten; hide applicant risk rating.
          const isPropertyApproved =
            String(co.applicant_status ?? '').toUpperCase() === 'PROPERTY_APPROVED'
          return (
            <div
              key={co.applicant_id}
              className={`relative min-w-0 overflow-hidden rounded-xl border-2 p-3 text-sm transition-all ${
                isActive
                  ? 'border-[#1597ff] bg-gradient-to-br from-sky-100 via-blue-50 to-white shadow-[0_10px_24px_rgba(21,151,255,0.18)]'
                  : 'border-slate-300 bg-white shadow-[0_6px_16px_rgba(15,23,42,0.06)] hover:border-slate-400 hover:shadow-[0_10px_22px_rgba(15,23,42,0.10)]'
              }`}
              data-testid={`application-detail-co-resident-tab-${index}`}
              data-active={isActive ? 'true' : 'false'}
            >
              {isActive ? (
                <span
                  className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[#1597ff]"
                  aria-hidden
                />
              ) : null}
              {isLocked && (
                <span
                  className="pointer-events-none absolute right-3 top-3 text-slate-600"
                  role="img"
                  aria-label="Applicant locked"
                  data-testid={`application-detail-co-resident-tab-${index}-lock-icon`}
                >
                  <Lock className="h-4 w-4" aria-hidden strokeWidth={2.25} />
                </span>
              )}
              <Link
                to={`/application/${co.applicant_id}`}
                className="block truncate pr-5 text-sm font-bold text-blue-700 hover:text-blue-800"
                data-testid={`application-detail-co-resident-tab-${index}-button`}
                onClick={(e) => handleNavigate(e, co.applicant_id)}
                title={`${co.user_data?.first_name} ${co.user_data?.last_name}`}
              >
                {co.user_data?.first_name} {co.user_data?.last_name}
              </Link>
              <dl className="mt-2 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs leading-snug">
                <dt className="font-semibold text-slate-700">Applicant</dt>
                <dd className="truncate font-normal text-slate-950">{formatApplicantId(Number(co.applicant_id))}</dd>
                <dt className="font-semibold text-slate-700">Primary</dt>
                <dd className="truncate font-normal text-slate-950">{co.is_primary ? 'Yes' : 'No'}</dd>
                <dt className="font-semibold text-slate-700">Status</dt>
                <dd
                  className="truncate font-normal text-slate-950"
                  title={
                    statusMap[String(co.applicant_status ?? "")] ||
                    co.applicant_status
                  }
                >
                  {statusMap[String(co.applicant_status ?? "")] ||
                    co.applicant_status}
                </dd>
                {rel ? (
                  <>
                    <dt className="font-semibold text-slate-700">Relationship</dt>
                    <dd className="truncate font-normal text-slate-950" title={rel}>{rel}</dd>
                  </>
                ) : null}
                <dt className="font-semibold text-slate-700">Underwrite Req</dt>
                <dd className="truncate font-normal text-slate-950">
                  {co.is_approved_require === '1' ? 'Yes' : 'No'}
                </dd>
                {livingValue !== null ? (
                  <>
                    <dt className="font-semibold text-slate-700">Living in Unit</dt>
                    <dd className="truncate font-normal text-slate-950">{livingValue}</dd>
                  </>
                ) : null}
                {showApplicantScreeningApproach &&
                co.screening_approach != null &&
                String(co.screening_approach).trim() !== '' ? (
                  <>
                    <dt className="font-semibold text-slate-700">Screening Approach</dt>
                    <dd
                      className="truncate font-normal text-slate-950"
                      title={String(co.screening_approach)}
                      data-testid={`application-detail-co-resident-tab-${index}-screening-approach`}
                    >
                      {String(co.screening_approach)}
                    </dd>
                  </>
                ) : null}
                {showApplicantRiskRating && !isPropertyApproved ? (
                  <>
                    <dt className="font-semibold text-slate-700">Risk Rating</dt>
                    <dd
                      className="min-w-0 font-normal text-slate-950"
                      data-testid={`application-detail-co-resident-tab-${index}-applicant-risk-rating`}
                    >
                      <RiskRatingBadge
                        value={
                          (co.risk_rating as string | undefined) ||
                          'Pending'
                        }
                        color={co.risk_rating ? co.risk_rating_color : null}
                      />
                      {co.risk_rating_error ? (
                        <p
                          className="mt-1 break-words text-xs leading-snug text-red-600"
                          title={String(co.risk_rating_error)}
                          data-testid={`application-detail-co-resident-tab-${index}-applicant-risk-rating-error`}
                        >
                          ({String(co.risk_rating_error)})
                        </p>
                      ) : null}
                    </dd>
                  </>
                ) : null}
              </dl>
            </div>
          )
        })}
      </div>
    </section>
  )
}
